import { useCallback, useMemo } from 'react'
import { DateTime } from 'luxon'
import { createFileRoute } from '@tanstack/react-router'
import { TaskPageLayout } from '../components/TaskPageLayout'
import { CaughtUpEmptyState } from '../components/CaughtUpEmptyState'
import { ListDetailsView } from '../components/ListDetailsView'
import { useTodayTasks } from '../hooks/useTodayTasks'
import type { TaskViewContext } from '../components/TaskPageLayout'
import type { TaskReorderEvent } from '../components/TaskList'
import type { ListDetailsViewSection } from '../components/ListDetailsView'
import { useTaskPageSetup } from '@/hooks'
import { useTaskMutations } from '@/lib/db/rxdb/hooks'
import useViewModeStore from '@/store/ViewModeStore'

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/tasks/today',
)({
  component: TodayPage,
})

/**
 * Today's tasks page component with overdue task management.
 * 
 * Displays tasks due today along with overdue tasks in separate sections.
 * Provides functionality to reschedule overdue tasks to today via drag-and-drop.
 * 
 * Features:
 * - Automatic separation of overdue vs today tasks
 * - Drag-and-drop to reschedule overdue tasks
 * - Collapsible overdue section
 * - Smart task filtering based on due dates
 * - Integrated task management capabilities
 * 
 * Route: `/$workspaceSlug/tasks/today`
 * 
 * @example
 * ```tsx
 * // Accessed via URL like: /my-workspace/tasks/today
 * // Shows tasks due today + overdue tasks in separate sections
 * ```
 */
function TodayPage() {
  useTaskPageSetup()

  const today = DateTime.now()
  const startOfToday = today.startOf('day').toJSDate()

  const showSubTasks = useViewModeStore((s) => s.showSubTasks)
  const { tasks, isLoading } = useTodayTasks({ includeSubTasks: showSubTasks })
  const { updateTask } = useTaskMutations()

  const renderTaskView = useCallback(
    (context: TaskViewContext) => (
      <TodayTaskList
        {...context}
        startOfToday={startOfToday}
        updateTask={updateTask}
      />
    ),
    [startOfToday, updateTask],
  )

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <TaskPageLayout
        tasks={tasks}
        isLoading={isLoading}
        defaultDueDate={startOfToday}
        renderTaskView={renderTaskView}
        showAddTaskInput={false}
        pageTitle="Today"
        renderEmptyState={(context) => <CaughtUpEmptyState view="today" {...context} />}
        navigateToSubtasks={true}
      />
    </div>
  )
}

export default TodayPage

/**
 * Props interface for TodayTaskList component.
 */
type TodayTaskListProps = TaskViewContext & {
  /** Start of today date for overdue calculation */
  startOfToday: Date
  /** Function to update task properties */
  updateTask: (id: string, updates: any) => Promise<void>
}

/**
 * Specialized task list component for Today page that handles overdue tasks.
 * 
 * Automatically separates tasks into "Overdue" and "Today" sections based on
 * due dates. Supports rescheduling overdue tasks to today via drag-and-drop.
 * 
 * @param props - Combined TaskViewContext props and Today-specific props
 */
function TodayTaskList({
  items,
  isLoading,
  defaultOnReorder,
  onSelect,
  selectedTaskId,
  containerClassName,
  itemClassName,
  formatDueDate,
  viewMode,
  startOfToday,
  updateTask,
}: TodayTaskListProps) {
  const { overdueTasks, todayTasks } = useMemo(() => {
    const overdue: typeof items = []
    const today: typeof items = []

    items.forEach((item) => {
      const dueDate = item.dueDate ? new Date(item.dueDate) : null

      if (dueDate && dueDate < startOfToday) {
        overdue.push(item)
        return
      }

      today.push(item)
    })

    return { overdueTasks: overdue, todayTasks: today }
  }, [items, startOfToday])

  const overdueTaskIdSet = useMemo(() => {
    return new Set(overdueTasks.map((task) => task.id))
  }, [overdueTasks])

  const scheduleTaskToToday = useCallback(
    async (taskId: string) => {
      const nextDueDate = new Date(startOfToday.getTime())
      await updateTask(taskId, {
        dueDate: nextDueDate.getTime(),
        dueDateType: '1',
      })
    },
    [startOfToday, updateTask],
  )

  const handleOverdueListReorder = useCallback(
    (event: TaskReorderEvent) => {
      if (!overdueTaskIdSet.has(event.sourceId)) {
        return
      }

      defaultOnReorder?.(event)
    },
    [defaultOnReorder, overdueTaskIdSet],
  )

  const handleTodayListReorder = useCallback(
    (event: TaskReorderEvent) => {
      if (overdueTaskIdSet.has(event.sourceId)) {
        scheduleTaskToToday(event.sourceId).catch((_error) => {
          // Error handling
        })
      }

      defaultOnReorder?.(event)
    },
    [defaultOnReorder, overdueTaskIdSet, scheduleTaskToToday],
  )

  const sections = useMemo<Array<ListDetailsViewSection>>(
    () => [
      {
        id: 'overdue',
        title: 'Overdue',
        items: overdueTasks,
        collapsible: true,
        defaultOpen: true,
        hideIfEmpty: true,
        onReorder: handleOverdueListReorder,
      },
      {
        id: 'today',
        title: overdueTasks.length > 0 ? 'Today' : '',
        items: todayTasks,
        onReorder: handleTodayListReorder,
      },
    ],
    [
      overdueTasks,
      todayTasks,
      handleOverdueListReorder,
      handleTodayListReorder,
    ],
  )

  return (
    <ListDetailsView
      items={items}
      isLoading={isLoading}
      defaultOnReorder={defaultOnReorder}
      onSelect={onSelect}
      selectedTaskId={selectedTaskId}
      containerClassName={containerClassName}
      itemClassName={itemClassName}
      formatDueDate={formatDueDate}
      viewMode={viewMode}
      sections={sections}
    />
  )
}
